/**
 * 1.4 P21: admin OTA rollout policy — window, cohorts, pinned stable per
 * family, per-family overview, event log; plus the per-controller
 * "Update now" card used by the admin controller inspector.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Switch, ActivityIndicator } from 'react-native';

import { api } from '../../api/client';
import { useTheme } from '../../context/ThemeContext';
import Input from '../Input';
import Picker from '../Picker';
import Button from '../Button';
import InlineConfirm from '../InlineConfirm';
import type { ColorTheme } from '../../utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../utils/theme';

interface Settings { window: { startHourLocal: number; endHourLocal: number }; cohorts: string[]; stable: Record<string, string> }
interface FamilyRow { family: string; stable: string | null; pinned: boolean; total: number; onStable: number; behind: number; online: number; pending: number; rolledBack: number }
interface OtaEvent { id: string; controllerId: string; fromVersion: string | null; toVersion: string | null; trigger: string; outcome: string; detail: Record<string, unknown>; createdAt: string }
interface FwVersion { version: string; family?: string; isStable?: boolean }

const FAMILY_LABEL: Record<string, string> = { dtwonder: 'RCP-Alpha-PoE', 'ws-8ro': 'RCP-Beta-PoE' };
const HOURS = Array.from({ length: 24 }, (_, h) => ({ value: String(h), label: `${h.toString().padStart(2, '0')}:00` }));

export function OtaPolicyCard({ notify }: { notify?: (type: 'success' | 'error', message: string) => void }) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [families, setFamilies] = useState<FamilyRow[]>([]);
  const [versions, setVersions] = useState<FwVersion[]>([]);
  const [events, setEvents] = useState<OtaEvent[]>([]);
  const [start, setStart] = useState('2');
  const [end, setEnd] = useState('5');
  const [cohortsText, setCohortsText] = useState('');
  const [stable, setStable] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      const [ov, ev, fw] = await Promise.all([
        api<{ settings: Settings; families: FamilyRow[] }>('/api/admin/ota/overview'),
        api<{ events: OtaEvent[] }>('/api/admin/ota/events?limit=40').catch(() => ({ events: [] as OtaEvent[] })),
        api<FwVersion[]>('/api/admin/firmware').catch(() => [] as FwVersion[]),
      ]);
      setSettings(ov.settings); setFamilies(ov.families); setEvents(ev.events); setVersions(Array.isArray(fw) ? fw : []);
      setStart(String(ov.settings.window.startHourLocal)); setEnd(String(ov.settings.window.endHourLocal));
      setCohortsText(ov.settings.cohorts.join(', ')); setStable(ov.settings.stable ?? {});
    } catch (err: any) { notify?.('error', err.message || 'Could not load the OTA policy'); }
    finally { setLoading(false); }
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const cohorts = cohortsText.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
    const bad = cohorts.find(c => !/^[0-9a-f-]{36}$/i.test(c));
    if (bad) { notify?.('error', `"${bad}" is not an owner id`); return; }
    setSaving(true);
    try {
      await api('/api/admin/ota/settings', { method: 'PUT', body: { window: { startHourLocal: parseInt(start), endHourLocal: parseInt(end) }, cohorts, stable: Object.fromEntries(Object.entries(stable).filter(([, v]) => v)) } });
      notify?.('success', 'OTA policy saved'); await load();
    } catch (err: any) { notify?.('error', err.message || 'Could not save'); }
    finally { setSaving(false); }
  };

  if (loading) return <ActivityIndicator color={colors.primary} style={{ marginVertical: spacing.lg }} />;
  if (!settings) return null;
  return (
    <View>
      <Text style={styles.sectionTitle}>OTA Rollout Policy</Text>
      <View style={styles.card}>
        <Text style={styles.help}>Boards update to the stable image on their first heartbeat after a claim, when a bay configuration needs a newer firmware, and nightly inside the window below (owner-local time) when they are online, idle, well connected and have free memory. Rollbacks raise an admin alert and stop retries until cleared.</Text>
        <View style={styles.row2}>
          <View style={{ flex: 1 }}><Picker label="Window start" options={HOURS} value={start} onValueChange={setStart} /></View>
          <View style={{ flex: 1 }}><Picker label="Window end" options={HOURS} value={end} onValueChange={setEnd} /></View>
        </View>
        <Input label="Cohorts (owner ids, comma separated; blank = every owner)" value={cohortsText} onChangeText={setCohortsText} autoCapitalize="none" placeholder="blank = all owners" />
        {families.map(f => {
          const opts = [{ value: '', label: 'Newest stable upload' }, ...versions.filter(v => (v.family ?? 'dtwonder') === f.family).map(v => ({ value: v.version, label: `${v.version}${v.isStable ? ' (stable)' : ''}` }))];
          return (
            <View key={f.family} style={styles.famRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.famTitle}>{FAMILY_LABEL[f.family] ?? f.family}</Text>
                <Text style={styles.famMeta}>Stable: {f.stable ?? 'none uploaded'}{f.pinned ? ' (pinned)' : ''} · {f.onStable}/{f.total} on stable · {f.behind} behind · {f.online} online · {f.pending} updating · {f.rolledBack} rolled back/failed</Text>
              </View>
              <View style={{ width: 220 }}><Picker options={opts} value={stable[f.family] ?? ''} onValueChange={v => setStable({ ...stable, [f.family]: v })} /></View>
            </View>
          );
        })}
        <Button title="Save policy" onPress={save} loading={saving} size="md" style={{ marginTop: spacing.sm, alignSelf: 'flex-start' }} />
      </View>

      <Text style={styles.sectionTitle}>OTA Events</Text>
      <View style={styles.card}>
        {events.length === 0 && <Text style={styles.help}>No OTA decisions logged yet.</Text>}
        {events.map(e => (
          <View key={e.id} style={styles.evRow}>
            <Text style={[styles.evOutcome, e.outcome === 'ok' && { color: colors.success }, (e.outcome === 'rollback' || e.outcome === 'failed') && { color: colors.danger }, e.outcome.startsWith('skipped') && { color: colors.textSecondary }]}>{e.outcome}</Text>
            <Text style={styles.evText} numberOfLines={1}>{e.controllerId} · {e.trigger} · {e.fromVersion ?? '?'} → {e.toVersion ?? '?'}</Text>
            <Text style={styles.evTime}>{new Date(e.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</Text>
          </View>
        ))}
      </View>
    </View>
  );
}

export function OtaUpdateNowCard({ controllerId, firmwareVersion, otaStatus, onDone, notify }: { controllerId: string; firmwareVersion: string | null; otaStatus: string | null; onDone?: () => void; notify?: (type: 'success' | 'error', message: string) => void }) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [force, setForce] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<string | null>(null);
  const run = async () => {
    setBusy(true); setConfirm(false);
    try {
      const r = await api<{ started: boolean; reason: string | null; toVersion: string | null }>(`/api/admin/controllers/${controllerId}/ota/update-now`, { method: 'POST', body: { force } });
      setLast(r.started ? `Update to ${r.toVersion} sent` : `Skipped: ${r.reason}${r.toVersion ? ` (stable ${r.toVersion})` : ''}`);
      notify?.(r.started ? 'success' : 'error', r.started ? `OTA to ${r.toVersion} started` : `Not started: ${r.reason}`);
      onDone?.();
    } catch (err: any) { notify?.('error', err.message || 'Update failed'); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    try { await api(`/api/admin/controllers/${controllerId}/ota/clear`, { method: 'POST', body: {} }); notify?.('success', 'OTA status cleared'); onDone?.(); }
    catch (err: any) { notify?.('error', err.message || 'Could not clear'); }
  };
  return (
    <View>
      <Text style={styles.sectionTitle}>Firmware policy</Text>
      <View style={styles.card}>
        <Text style={styles.help}>Running {firmwareVersion ?? 'unknown'}{otaStatus ? ` · OTA status: ${otaStatus}` : ''}. "Update now" sends the family's stable image after the safety checks (online, idle, signal, memory); Force skips the checks except family and pending.</Text>
        <View style={styles.switchRow}>
          <Text style={styles.switchLabel}>Force (skip preconditions)</Text>
          <Switch value={force} onValueChange={setForce} trackColor={{ false: colors.borderLight, true: colors.danger + '80' }} thumbColor={force ? colors.danger : colors.textLight} />
        </View>
        <View style={styles.row2}>
          <Button title="Update now" onPress={() => (force ? setConfirm(true) : run())} loading={busy} size="sm" variant={force ? 'danger' : 'primary'} />
          {(otaStatus === 'rolled_back' || otaStatus === 'failed') && <Button title="Clear status" onPress={clear} size="sm" variant="outline" />}
        </View>
        <InlineConfirm visible={confirm} message="Force skips the idle / signal / memory checks. A wash in progress on this board would be interrupted. Send the update anyway?" confirmLabel="Send update" onConfirm={run} onCancel={() => setConfirm(false)} />
        {!!last && <Text style={styles.help}>{last}</Text>}
      </View>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginTop: spacing.lg, marginBottom: spacing.sm },
  card: { backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md, borderWidth: 1, borderColor: c.borderLight },
  help: { fontSize: fontSize.xs, color: c.textSecondary, marginBottom: spacing.sm },
  row2: { flexDirection: 'row', gap: spacing.sm, flexWrap: 'wrap', alignItems: 'center' },
  famRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: c.borderLight, flexWrap: 'wrap' },
  famTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  famMeta: { fontSize: fontSize.xs, color: c.textSecondary },
  evRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: 4, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: c.borderLight },
  evOutcome: { width: 120, fontSize: 11, fontWeight: fontWeight.semibold, color: c.text },
  evText: { flex: 1, fontSize: 11, color: c.text },
  evTime: { fontSize: 10, color: c.textSecondary },
  switchRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing.md, paddingVertical: spacing.xs },
  switchLabel: { fontSize: fontSize.sm, color: c.text },
});
